import {
  cloneDeep,
  each,
  first,
  indexOf,
  intersection,
  last,
  union,
  values,
  uniq,
} from 'lodash'
import {toJS} from 'mobx'
import {
  CreditCardPaymentMethods,
  getCreditCardType,
  isCreditCardPaymentMethod,
  Payment,
  PaymentConfig,
  PaymentCurrency,
  PaymentErrors,
  PaymentMethod,
  PaymentType,
  PseudoRecurringPaymentMethods,
  RecurringInterval,
  RecurringPaymentMethods,
} from '@raisenow/epik'
import {WidgetApi} from '../WidgetApi'
import {ComputedField} from '../Compiler'
import {ValidationErrors} from '../Validator'
import {PaymentSlipDeliveryType} from '../PaymentSlipDeliveryType'
import {Block} from '../Block'
import {IPaymentForm} from './IPaymentForm'
import {
  parseAmount,
  formatAmount,
  getAmountSubunitFactor,
  unitsToSubunits,
} from '../helpers'

///////////////////////////////////////////////////////////////////////////////

export type PaymentFormBaseData = {
  purpose: string
  stored_campaign_id: string
  stored_campaign_subid: string
  stored_cover_transaction_fee: boolean
  payment_type: PaymentType
  recurring_interval: RecurringInterval
  currency: PaymentCurrency
  amount: number
  amount_position: number
  custom_amount: string
  payment_method: PaymentMethod
  last_selected_payment_method: PaymentMethod
  [key: string]: string | number | boolean
}

export type PaymentFormBaseArgs = {
  api: WidgetApi
}

///////////////////////////////////////////////////////////////////////////////

export abstract class PaymentFormBase implements IPaymentForm {
  api: WidgetApi
  data: PaymentFormBaseData
  private payment: Payment

  async init(args: PaymentFormBaseArgs) {
    let {api} = args

    this.api = api
    this.resetPayment()
    this.addValidationRules()
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // GETTERS (main)
  //
  /////////////////////////////////////////////////////////////////////////////

  getPurpose(): string {
    return this.data.purpose
  }

  getPaymentType(): PaymentType {
    return this.data.payment_type
  }

  getRecurringInterval(): RecurringInterval {
    return this.data.recurring_interval
  }

  getCurrency(): PaymentCurrency {
    return this.data.currency
  }

  getAmount(): number {
    return this.data.amount
  }

  getAmountPosition(): number {
    return this.data.amount_position
  }

  getFeeAmount(): number {
    const amountSubunitFactor = getAmountSubunitFactor(
      this.getCurrency(),
      this.api.computedConfig,
    )
    const amount = this.getAmount()
    const fee = this.getFeeFixed() + (this.getFeePercentage() * amount) / 100

    return Math.ceil(fee * amountSubunitFactor) / amountSubunitFactor
  }

  getFeeFixed(): number {
    return this.api.resolveConfigWithSingleResult<number>('coverFeeFixed') || 0
  }

  getFeePercentage(): number {
    return (
      this.api.resolveConfigWithSingleResult<number>('coverFeePercentage') || 0
    )
  }

  getTotalAmount(): number {
    if (!this.data.stored_cover_transaction_fee) {
      return this.getAmount()
    }

    return this.getAmount() + this.getFeeAmount()
  }

  getCustomAmount(): string {
    return this.data.custom_amount
  }

  getPaymentMethod(): PaymentMethod {
    return this.data.payment_method
  }

  getLastSelectedPaymentMethod(): PaymentMethod {
    return this.data.last_selected_payment_method
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // SETTERS (main)
  //
  /////////////////////////////////////////////////////////////////////////////

  setPurpose(purpose: string, adjust: boolean = true) {
    this.data.purpose = purpose
    this.setPurposeDetails(purpose)

    adjust && this.adjustState()
  }

  setPurposeDetails(purpose: string) {
    const {computedConfig} = this.api

    this.data.stored_campaign_id = null
    this.data.stored_campaign_subid = null

    const purposeDetails =
      computedConfig.purposeDetails && computedConfig.purposeDetails[purpose]

    if (purposeDetails) {
      const {stored_campaign_id, stored_campaign_subid} = purposeDetails

      if (stored_campaign_id) {
        this.data.stored_campaign_id = stored_campaign_id
      }

      if (stored_campaign_subid) {
        this.data.stored_campaign_subid = stored_campaign_subid
      }
    }
  }

  setPaymentType(paymentType: PaymentType, adjust: boolean = true) {
    this.data.payment_type = paymentType
    adjust && this.adjustState()
  }

  setRecurringInterval(
    recurringInterval: RecurringInterval,
    adjust: boolean = true,
  ) {
    this.data.recurring_interval = recurringInterval
    adjust && this.adjustState()
  }

  setCurrency(currency: PaymentCurrency, adjust: boolean = true) {
    this.data.currency = currency
    adjust && this.adjustState()
  }

  setAmount(amount: number | string | null, adjust: boolean = true) {
    const value = parseAmount(
      amount,
      this.getCurrency(),
      this.api.computedConfig,
    )

    this.data.amount = value
    this.data.amount_position = indexOf(toJS(this.getPossibleAmounts()), value)

    if (value) {
      if (this.isPossibleAmount(value)) {
        this.setCustomAmount(null)
      } else {
        this.setCustomAmount(
          formatAmount(value, this.getCurrency(), this.api.computedConfig),
        )
      }
    } else {
      this.setCustomAmount(null)
    }

    adjust && this.adjustState()
  }

  setCustomAmount(customAmount: string) {
    this.data.custom_amount = customAmount
  }

  setPaymentMethod(paymentMethod: PaymentMethod, adjust: boolean = true) {
    this.data.payment_method = paymentMethod
    adjust && this.adjustState()
  }

  setLastSelectedPaymentMethod(paymentMethod: PaymentMethod) {
    this.data.last_selected_payment_method = paymentMethod
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // GETTERS (getPossible... plural)
  //
  /////////////////////////////////////////////////////////////////////////////

  getPossiblePurposes(): string[] {
    return this.api.resolveConfig('purposes')
  }

  getPossiblePaymentTypes(): PaymentType[] {
    return this.api.resolveConfig<PaymentType>('paymentTypes')
  }

  getPossibleRecurringIntervals(): RecurringInterval[] {
    return this.api.resolveConfig<RecurringInterval>('recurringIntervals')
  }

  getPossibleCurrencies(): PaymentCurrency[] {
    return this.api.resolveConfig<PaymentCurrency>('currencies')
  }

  getPossibleAmounts(): number[] {
    const amounts = this.api
      .resolveConfig<string>('amounts')
      .map((v) => parseAmount(v, this.getCurrency(), this.api.computedConfig))
      .filter((v) => !!v)

    return uniq(amounts)
  }

  getPossiblePaymentMethods(): PaymentMethod[] {
    let paymentMethods = this.api.resolveConfig<PaymentMethod>('paymentMethods')
    let allowedRecurringPaymentMethods = this.api.resolveConfig<PaymentMethod>(
      'allowedRecurringPaymentMethods',
    )

    if (this.getPaymentType() === PaymentType.RECURRING) {
      paymentMethods = intersection(
        paymentMethods,
        allowedRecurringPaymentMethods,
        union(RecurringPaymentMethods, PseudoRecurringPaymentMethods),
      )
    }

    // make sure payment method "cc" is always in the list if any of the credit
    // card payment methods is allowed
    if (
      intersection(paymentMethods, CreditCardPaymentMethods).length > 0 &&
      paymentMethods.indexOf(PaymentMethod.CREDIT_CARD) === -1
    ) {
      let firstCreditCardPaymentMethodIndex = CreditCardPaymentMethods.map(
        (pm) => paymentMethods.indexOf(pm),
      ) // gather positions of credit card payment methods
        .filter((index) => index !== -1) // remove -1 - missing payment methods
        .sort((index1, index2) => index1 - index2)[0] // sort properly and take first

      // prepend payment method cc before the very first credit card payment method
      paymentMethods.splice(
        firstCreditCardPaymentMethodIndex,
        0,
        PaymentMethod.CREDIT_CARD,
      )
    }

    // only "cc" is in the list, have to add all the other
    if (
      paymentMethods.indexOf(PaymentMethod.CREDIT_CARD) !== -1 &&
      intersection(paymentMethods, CreditCardPaymentMethods).length === 1
    ) {
      let firstCreditCardPaymentMethodIndex = paymentMethods.indexOf(
        PaymentMethod.CREDIT_CARD,
      )

      // add all credit card types after the initial credit card type, omit the "cc" type - duplicate
      paymentMethods.splice(
        firstCreditCardPaymentMethodIndex,
        0,
        ...CreditCardPaymentMethods.filter(
          (pm) => pm !== PaymentMethod.CREDIT_CARD,
        ),
      )
    }

    if (this.isPaymentSlipForbidden()) {
      paymentMethods = paymentMethods.filter(
        (v) =>
          ![
            PaymentMethod.PAYMENT_SLIP,
            PaymentMethod.PAYMENT_SLIP_WITH_REFERENCE_NUMBER,
          ].includes(v),
      )
    }

    return paymentMethods
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // GETTERS (getPossible... singular)
  //
  /////////////////////////////////////////////////////////////////////////////

  getFirstPossiblePaymentMethod(): PaymentMethod {
    return first(this.getPossiblePaymentMethods())
  }

  getFirstPossiblePaymentType(): PaymentType {
    return first(this.getPossiblePaymentTypes())
  }

  getFirstPossibleRecurringInterval(): RecurringInterval {
    return first(this.getPossibleRecurringIntervals())
  }

  getFirstPossibleAmount(): number {
    return first(this.getPossibleAmounts())
  }

  getPossibleAmountByIdx(idx: number): number {
    return first(this.getPossibleAmounts())
  }

  getFirstPossiblePurpose(): string {
    return first(this.getPossiblePurposes())
  }

  getFirstPossibleCurrency(): PaymentCurrency {
    return first(this.getPossibleCurrencies())
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // OTHER (isPossible...)
  //
  /////////////////////////////////////////////////////////////////////////////

  isPossiblePaymentMethod(paymentMethod: PaymentMethod): boolean {
    return this.getPossiblePaymentMethods().indexOf(paymentMethod) !== -1
  }

  isPossiblePaymentType(paymentType: PaymentType): boolean {
    return this.getPossiblePaymentTypes().indexOf(paymentType) !== -1
  }

  isPossibleRecurringInterval(recurringInterval: RecurringInterval): boolean {
    return (
      this.getPossibleRecurringIntervals().indexOf(recurringInterval) !== -1
    )
  }

  isPossibleAmount(amount: number): boolean {
    return this.getPossibleAmounts().indexOf(amount) !== -1
  }

  isPossiblePurpose(purpose: string): boolean {
    return this.getPossiblePurposes().indexOf(purpose) !== -1
  }

  isPossibleCurrency(currency: PaymentCurrency): boolean {
    return this.getPossibleCurrencies().indexOf(currency) !== -1
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // getDefault...
  //
  /////////////////////////////////////////////////////////////////////////////

  getDefaultPurpose(): string {
    return this.api.resolveConfigWithSingleResult<string>('defaultPurpose')
  }

  getDefaultPaymentType(): PaymentType {
    return this.api.resolveConfigWithSingleResult<PaymentType>(
      'defaultPaymentType',
    )
  }

  getDefaultRecurringInterval(): RecurringInterval {
    return this.api.resolveConfigWithSingleResult<RecurringInterval>(
      'defaultRecurringInterval',
    )
  }

  getDefaultCurrency(): PaymentCurrency {
    return this.api.resolveConfigWithSingleResult<PaymentCurrency>(
      'defaultCurrency',
    )
  }

  getDefaultAmount(): number {
    return this.api.resolveConfigWithSingleResult<number>('defaultAmount')
  }

  getDefaultPaymentMethod(): PaymentMethod {
    return this.api.resolveConfigWithSingleResult<PaymentMethod>(
      'defaultPaymentMethod',
    )
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // autocorrect...
  //
  /////////////////////////////////////////////////////////////////////////////

  autocorrectPurpose(allowBlank = true) {
    const {autoselectPurpose} = this.api.computedConfig
    const purpose = this.getPurpose()
    const defaultPurpose = this.getDefaultPurpose()
    const firstPossiblePurpose = this.getFirstPossiblePurpose()
    const isBlankPurpose = purpose === null || purpose === undefined

    if (allowBlank && isBlankPurpose) {
      this.setPurpose(null, false)
      return
    }

    if (!this.isPossiblePurpose(purpose)) {
      if (this.isPossiblePurpose(defaultPurpose)) {
        this.setPurpose(defaultPurpose, false)
      } else if (autoselectPurpose) {
        this.setPurpose(firstPossiblePurpose, false)
      } else {
        this.setPurpose(null, false)
      }
    }
  }

  autocorrectPaymentType(allowBlank = true) {
    const {autoselectPaymentType} = this.api.computedConfig
    const paymentType = this.getPaymentType()
    const defaultPaymentType = this.getDefaultPaymentType()
    const firstPossiblePaymentType = this.getFirstPossiblePaymentType()
    const isBlankPaymentType = paymentType === null || paymentType === undefined

    if (allowBlank && isBlankPaymentType) {
      this.setPaymentType(null, false)
      return
    }

    if (!this.isPossiblePaymentType(paymentType)) {
      if (this.isPossiblePaymentType(defaultPaymentType)) {
        this.setPaymentType(defaultPaymentType, false)
      } else if (autoselectPaymentType) {
        this.setPaymentType(firstPossiblePaymentType, false)
      } else {
        this.setPaymentType(null, false)
      }
    }
  }

  autocorrectRecurringInterval(allowBlank = true) {
    const {autoselectRecurringInterval} = this.api.computedConfig
    const recurringInterval = this.getRecurringInterval()
    const defaultRecurringInterval = this.getDefaultRecurringInterval()
    const firstPossibleRecurringInterval =
      this.getFirstPossibleRecurringInterval()
    const isBlankRecurringInterval =
      recurringInterval === null || recurringInterval === undefined

    if (allowBlank && isBlankRecurringInterval) {
      this.setRecurringInterval(null, false)
      return
    }

    if (!this.isPossibleRecurringInterval(recurringInterval)) {
      if (this.isPossibleRecurringInterval(defaultRecurringInterval)) {
        this.setRecurringInterval(defaultRecurringInterval, false)
      } else if (autoselectRecurringInterval) {
        this.setRecurringInterval(firstPossibleRecurringInterval, false)
      } else {
        this.setRecurringInterval(null, false)
      }
    }
  }

  autocorrectCurrency(allowBlank = true) {
    const {autoselectCurrency} = this.api.computedConfig
    const currency = this.getCurrency()
    const defaultCurrency = this.getDefaultCurrency()
    const firstPossibleCurrency = this.getFirstPossibleCurrency()
    const isBlankCurrency = currency === null || currency === undefined

    if (allowBlank && isBlankCurrency) {
      this.setCurrency(null, false)
      return
    }

    if (!this.isPossibleCurrency(currency)) {
      if (this.isPossibleCurrency(defaultCurrency)) {
        this.setCurrency(defaultCurrency, false)
      } else if (autoselectCurrency) {
        this.setCurrency(firstPossibleCurrency, false)
      } else {
        this.setCurrency(null, false)
      }
    }
  }

  autocorrectAmount() {
    const {autoselectAmount} = this.api.computedConfig
    let newAmount = null

    // get position of previously selected amount in a list
    const amountPosition = this.getAmountPosition()

    if (amountPosition !== null && amountPosition !== -1) {
      // Possible amount previously was selected.
      // - we should select new one on the same position
      // - if it's impossible then select last one
      // - if it's impossible then set to null
      const possibleAmounts = this.getPossibleAmounts()
      const positionedAmount = possibleAmounts[amountPosition]
      const lastAmount = last(possibleAmounts)

      if (positionedAmount) {
        newAmount = positionedAmount
      } else if (lastAmount) {
        newAmount = lastAmount
      } else {
        newAmount = null
      }

      this.setAmount(newAmount, false)
    } else {
      // Possible amount previously was unselected or custom_amount was selected.
      // - we should select default amount (if configured)
      // - if it's impossible then select first one (if autoselectAmount is enabled)
      // - if it's impossible then set to null
      const amount = this.getAmount()
      const allowCustomAmount = this.allowCustomAmount()
      const isCustomAmount = this.isCustomAmount(amount)
      const defaultAmount = this.getDefaultAmount()
      const firstPossibleAmount = this.getFirstPossibleAmount()

      if (allowCustomAmount && isCustomAmount) {
        return
      }

      if (this.isPossibleAmount(defaultAmount)) {
        newAmount = defaultAmount
      } else if (autoselectAmount) {
        newAmount = firstPossibleAmount
      } else {
        newAmount = null
      }

      this.setAmount(newAmount, false)
    }

    if (this.data['stored_rnw_analytics_preselected_amount'] === undefined) {
      this.data['stored_rnw_analytics_preselected_amount'] = unitsToSubunits(
        newAmount,
        this.getCurrency(),
        this.api.computedConfig,
      )
    }
  }

  autocorrectPaymentMethod(allowBlank = true) {
    let {autoselectPaymentMethod} = this.api.computedConfig
    let paymentMethod = this.getPaymentMethod()
    let defaultPaymentMethod = this.getDefaultPaymentMethod()
    let firstPossiblePaymentMethod = this.getFirstPossiblePaymentMethod()
    let isBlankPaymentMethod =
      paymentMethod === null || paymentMethod === undefined

    if (allowBlank && isBlankPaymentMethod) {
      this.setPaymentMethod(null, false)
      return
    }

    if (!this.isPossiblePaymentMethod(paymentMethod)) {
      if (isCreditCardPaymentMethod(paymentMethod)) {
        return
      }

      if (this.isPossiblePaymentMethod(defaultPaymentMethod)) {
        this.setPaymentMethod(defaultPaymentMethod, false)
      } else if (autoselectPaymentMethod) {
        this.setPaymentMethod(firstPossiblePaymentMethod, false)
      } else {
        this.setPaymentMethod(null, false)
      }
    }
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // OTHER (custom amounts)
  //
  /////////////////////////////////////////////////////////////////////////////

  clearAmount() {
    this.data.amount = null

    if (this.data['stored_rnw_analytics_preselected_amount'] !== undefined) {
      this.data['stored_rnw_analytics_preselected_amount'] = undefined
    }
  }

  clearCustomAmount() {
    this.data.custom_amount = null
  }

  allowCustomAmount(): boolean {
    return this.api.resolveConfigWithSingleResult<boolean>('allowCustomAmount')
  }

  getMinimumCustomAmount(): number {
    return this.api.resolveConfigWithSingleResult<number>('minimumCustomAmount')
  }

  getMaximumCustomAmount(): number {
    return this.api.resolveConfigWithSingleResult<number>('maximumCustomAmount')
  }

  isCustomAmount(amount: number | string): boolean {
    const value = parseAmount(
      amount,
      this.getCurrency(),
      this.api.computedConfig,
    )

    return !!value && !this.isPossibleAmount(value)
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // ADJUST STATE
  //
  /////////////////////////////////////////////////////////////////////////////

  adjustState(rerun: boolean = true) {
    this.autocorrectPurpose()
    this.autocorrectPaymentType()
    this.autocorrectRecurringInterval()
    this.autocorrectCurrency()
    this.autocorrectAmount()
    this.autocorrectPaymentMethod()

    // rerun procedure one more time to fix possible errors
    // coming from the order of adjustment blocks above
    rerun && this.adjustState(false)
  }

  /////////////////////////////////////////////////////////////////////////////

  private addValidationRules() {
    let {validator} = this.api

    validator.addRule('minimum_custom_amount', ({value, ctx}) => {
      if (!ctx) {
        return
      }

      let min = ctx.getMinimumCustomAmount()

      if (value && min && value < min && ctx.isCustomAmount(value)) {
        return {
          code: 'minimum_custom_amount',
          message: `Minimum amount is ${min}`,
          data: {min: min},
        }
      }
    })

    validator.addRule('maximum_custom_amount', ({value, ctx}) => {
      if (!ctx) {
        return
      }

      let max = ctx.getMaximumCustomAmount()

      if (value && max && value > max && ctx.isCustomAmount(value)) {
        return {
          code: 'maximum_custom_amount',
          message: `Maximum amount is ${max}`,
          data: {max: max},
        }
      }
    })

    validator.addRule('credit_card_type_supported', ({ctx}) => {
      if (!ctx) {
        return
      }

      let isCC = isCreditCardPaymentMethod(ctx.getPaymentMethod())
      let ccType = getCreditCardType(
        ctx.getPaymentConfig().cardno,
        ctx.getPaymentConfig().flow,
      )
      let isPossibleCC = ctx.isPossiblePaymentMethod(ccType)

      if (isCC && ccType != null && !isPossibleCC) {
        return {
          code: 'invalid_credit_card_type',
          message: 'Invalid credit card type',
        }
      }
    })
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // PAYMENT (getPayment, updatePayment, ...)
  //
  /////////////////////////////////////////////////////////////////////////////

  getPayment(): Payment {
    let {epik} = this.api

    if (!this.payment) {
      this.payment = epik.createPayment()
    }

    this.payment.replace(this.getPaymentConfigWithComputations())

    return this.payment
  }

  resetPayment() {
    this.payment = null
  }

  getPaymentConfigWithComputations(): PaymentConfig {
    return {
      ...this.getPaymentConfig(),
      ...this.getPaymentConfigComputations(),
    }
  }

  abstract getPaymentConfig(): PaymentConfig

  getPaymentConfigComputations(): Partial<PaymentConfig> {
    let computations = this.api.resolveConfig<ComputedField>(
      'paymentComputations',
    )
    let fields = {}

    each(computations, (field) => {
      let result = this.api.compiler.compile(field.format)
      let name = field.field

      fields[name] = result
    })

    return fields
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // VALIDATION HELPERS
  //
  /////////////////////////////////////////////////////////////////////////////

  omitHiddenFieldErrors(
    errors: ValidationErrors & PaymentErrors,
  ): ValidationErrors & PaymentErrors {
    errors = cloneDeep(errors)

    const showStoredCustomerSalutation =
      this.api.resolveConfigWithSingleResult<boolean>(
        'showStoredCustomerSalutation',
      )
    const showStoredCustomerFirstname =
      this.api.resolveConfigWithSingleResult<boolean>(
        'showStoredCustomerFirstname',
      )
    const showStoredCustomerLastname =
      this.api.resolveConfigWithSingleResult<boolean>(
        'showStoredCustomerLastname',
      )
    const showStoredCustomerEmail =
      this.api.resolveConfigWithSingleResult<boolean>('showStoredCustomerEmail')
    const showStoredCustomerBirthdate =
      this.api.resolveConfigWithSingleResult<boolean>(
        'showStoredCustomerBirthdate',
      )
    const showStoredCustomerEmailPermission =
      this.api.resolveConfigWithSingleResult<boolean>(
        'showStoredCustomerEmailPermission',
      )
    const showStoredCustomerMessage =
      this.api.resolveConfigWithSingleResult<boolean>(
        'showStoredCustomerMessage',
      )
    const showStoredCustomerDonationReceipt =
      this.api.resolveConfigWithSingleResult<boolean>(
        'showStoredCustomerDonationReceipt',
      )
    const showStoredCustomerStreet =
      this.api.resolveConfigWithSingleResult<boolean>(
        'showStoredCustomerStreet',
      )
    const showStoredCustomerStreetNumber =
      this.api.resolveConfigWithSingleResult<boolean>(
        'showStoredCustomerStreetNumber',
      )
    const showStoredCustomerStreet2 =
      this.api.resolveConfigWithSingleResult<boolean>(
        'showStoredCustomerStreet2',
      )
    const showStoredCustomerPobox =
      this.api.resolveConfigWithSingleResult<boolean>('showStoredCustomerPobox')
    const showStoredCustomerZipCode =
      this.api.resolveConfigWithSingleResult<boolean>(
        'showStoredCustomerZipCode',
      )
    const showStoredCustomerCity =
      this.api.resolveConfigWithSingleResult<boolean>('showStoredCustomerCity')
    const showStoredCustomerCountry =
      this.api.resolveConfigWithSingleResult<boolean>(
        'showStoredCustomerCountry',
      )
    const showStoredCustomerState = this.showStoredCustomerState()
    const showBankbic =
      this.api.resolveConfigWithSingleResult<boolean>('showBankbic')
    if (!showStoredCustomerSalutation) {
      delete errors.stored_customer_salutation
    }
    if (!showStoredCustomerFirstname) {
      delete errors.stored_customer_firstname
    }
    if (!showStoredCustomerLastname) {
      delete errors.stored_customer_lastname
    }
    if (!showStoredCustomerEmail) {
      delete errors.stored_customer_email
    }
    if (!showStoredCustomerBirthdate) {
      delete errors.stored_customer_birthdate
    }
    if (!showStoredCustomerEmailPermission) {
      delete errors.stored_customer_email_permission
    }
    if (!showStoredCustomerMessage) {
      delete errors.stored_customer_message
    }
    if (!showStoredCustomerDonationReceipt) {
      delete errors.stored_customer_donation_receipt
    }
    if (!showStoredCustomerStreet) {
      delete errors.stored_customer_street
    }
    if (!showStoredCustomerStreetNumber) {
      delete errors.stored_customer_street_number
    }
    if (!showStoredCustomerStreet2) {
      delete errors.stored_customer_street2
    }
    if (!showStoredCustomerPobox) {
      delete errors.stored_customer_pobox
    }
    if (!showStoredCustomerZipCode) {
      delete errors.stored_customer_zip_code
    }
    if (!showStoredCustomerCity) {
      delete errors.stored_customer_city
    }
    if (!showStoredCustomerCountry) {
      delete errors.stored_customer_country
    }
    if (!showStoredCustomerState) {
      delete errors.stored_customer_state
    }
    if (!showBankbic) {
      delete errors.bankbic
    }

    return errors
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // PAYMENT SLIP DELIVERY TYPE HELPERS
  //
  /////////////////////////////////////////////////////////////////////////////

  getPossiblePaymentSlipDeliveryTypes(): PaymentSlipDeliveryType[] {
    let allPossiblePaymentSlipDeliveryTypes = values(
      PaymentSlipDeliveryType,
    ) as PaymentSlipDeliveryType[]
    let paymentSlipDeliveryTypes =
      this.api.resolveConfig<PaymentSlipDeliveryType>(
        'paymentSlipDeliveryTypes',
      )

    // filter out unsupported delivery types
    paymentSlipDeliveryTypes = intersection(
      paymentSlipDeliveryTypes,
      allPossiblePaymentSlipDeliveryTypes,
    )

    // if misconfigured then fallback to all possible delivery types
    if (paymentSlipDeliveryTypes.length === 0) {
      paymentSlipDeliveryTypes = allPossiblePaymentSlipDeliveryTypes
    }

    return paymentSlipDeliveryTypes
  }

  getDefaultPaymentSlipDeliveryType(): PaymentSlipDeliveryType {
    let paymentSlipDeliveryTypes = this.getPossiblePaymentSlipDeliveryTypes()
    let defaultPaymentSlipDeliveryType =
      this.api.resolveConfigWithSingleResult<PaymentSlipDeliveryType>(
        'defaultPaymentSlipDeliveryType',
      )

    // if default value is unsupported then fallback to first one
    if (
      indexOf(paymentSlipDeliveryTypes, defaultPaymentSlipDeliveryType) === -1
    ) {
      defaultPaymentSlipDeliveryType = paymentSlipDeliveryTypes[0]
    }

    return defaultPaymentSlipDeliveryType
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // STATES HELPERS
  //
  /////////////////////////////////////////////////////////////////////////////

  getStates(): string[] | undefined {
    const statesByCountry = toJS(this.api.computedConfig.states)
    const country = this.data['stored_customer_country'] as string

    return statesByCountry[country]
  }

  showStoredCustomerState(): boolean {
    const showStoredCustomerState =
      this.api.resolveConfigWithSingleResult<boolean>('showStoredCustomerState')
    const states = this.getStates()

    return !!(showStoredCustomerState && states?.length > 0)
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // COVER FEE HELPERS
  //
  /////////////////////////////////////////////////////////////////////////////

  isPaymentCoverFeeEnabled(): boolean {
    return this.api
      .resolveConfig('paymentWidgetBlocks')
      .includes(Block.PAYMENT_COVER_FEE)
  }

  /////////////////////////////////////////////////////////////////////////////

  isPaymentSlipForbidden(): boolean {
    const now = Date.now()
    const forbidAfter = Date.UTC(2022, 9, 1, 0, 0, 0)

    return now > forbidAfter
  }
}
